import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CopyableTraceIdProps {
  traceId: string;
  className?: string;
}

export const CopyableTraceId: React.FC<CopyableTraceIdProps> = ({ traceId, className }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(traceId);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = traceId;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy trace ID:', err);
    }
  };

  return (
    <div
      className={cn(
        'mt-1.5 flex flex-col gap-1.5 rounded-md bg-neutral-50 p-2 border border-neutral-200 text-xs text-neutral-700 select-none',
        className
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold text-neutral-500 text-[11px] uppercase tracking-wider">
          Trace ID
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-all duration-150 focus:outline-none focus:ring-1 focus:ring-primary-500 cursor-pointer',
            copied
              ? 'bg-green-100 text-green-800 border border-green-300'
              : 'bg-white text-neutral-700 border border-neutral-300 hover:bg-neutral-100 hover:text-neutral-900 shadow-2xs active:scale-95'
          )}
          title="Copy Trace ID"
          aria-label="Copy Trace ID"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 text-green-700" />
              <span>Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3 text-neutral-500" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
      <div
        className="font-mono text-[11px] text-neutral-700 break-all select-all bg-white p-1.5 rounded border border-neutral-200 leading-relaxed"
        title={traceId}
      >
        {traceId}
      </div>
    </div>
  );
};
